/** @format */

import { DEFAULT_FILTERS } from '@/@ducks/listing/constants';
import { selectorFilters } from '@/@ducks/listing/selectors';
import { useAppSelector } from '@/redux/hooks';
import MapboxGeocoder, { GeocoderOptions } from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ControlPosition, Marker, MarkerProps, useControl, useMap } from 'react-map-gl';
import { SinglePin } from './Pin';
import { StyledGeocoder } from './Geocoder.style';

type GeocoderControlProps = Omit<GeocoderOptions, 'accessToken' | 'mapboxgl' | 'marker'> & {
  mapboxAccessToken: string;
  marker?: boolean | Omit<MarkerProps, 'longitude' | 'latitude'>;
  position: ControlPosition;
  onLoading?: (e: object) => void;
  onResults?: (e: object) => void;
  onResult?: (e: object) => void;
  onError?: (e: object) => void;
};

export default function GeocoderControl(props: GeocoderControlProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { current: map } = useMap();
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const previousZoomRef = React.useRef<number | null>(null);
  const filters = useAppSelector(selectorFilters);
  const defaultPageFilters = useMemo(() => {
    return {
      ...DEFAULT_FILTERS,
    };
  }, []);

  const isDefaultFilters = useMemo(() => {
    return isEqual(filters, defaultPageFilters);
  }, [filters, defaultPageFilters]);

  const [markerPosition, setMarkerPosition] = useState<{ longitude: number; latitude: number } | null>(null);

  const geocoder = useControl<MapboxGeocoder>(
    () => {
      const ctrl = new MapboxGeocoder({
        ...props,
        marker: false,
        accessToken: props.mapboxAccessToken,
      });
      ctrl.on('loading', props.onLoading || (() => {}));
      ctrl.on('results', props.onResults || (() => {}));
      ctrl.on('result', evt => {
        if (props.onResult) {
          props.onResult(evt);
        }

        const { result } = evt;
        const location =
          result && (result.center || (result.geometry?.type === 'Point' && result.geometry.coordinates));
        if (location && props.marker) {
          setMarkerPosition({ longitude: location[0], latitude: location[1] });
        }
      });
      ctrl.on('error', props.onError || (() => {}));
      return ctrl;
    },
    {
      position: props.position,
    },
  );

  useEffect(() => {
    const input = document.querySelector<HTMLInputElement>('.mapboxgl-ctrl-geocoder--input');

    if (input && isMobileDevice) {
      inputRef.current = input;
      inputRef.current.style.opacity = '1';
      input.disabled = true;

      if (isIOS) {
        // add meta viewport tag to prevent zooming
        const viewportMeta = document.querySelector('meta[name=viewport]');
        if (viewportMeta) {
          viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
        }

        // store the current zoom level before input focus
        const handleFocus = () => {
          input.disabled = false;
          if (map) {
            previousZoomRef.current = map.getZoom();
          }
        };

        // restore the previous zoom level after input
        const handleBlur = () => {
          if (map && previousZoomRef.current !== null) {
            map.setZoom(previousZoomRef.current);
            previousZoomRef.current = null;
          }
          input.disabled = true;
        };

        input.addEventListener('touchend', handleFocus);
        input.addEventListener('blur', handleBlur);

        return () => {
          input.removeEventListener('focus', handleFocus);
          input.removeEventListener('blur', handleBlur);
          // restore original viewport meta
          if (viewportMeta) {
            viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1');
          }
        };
      } else {
        const enableInput = () => {
          input.disabled = false;
          input.focus();
        };

        const disableInput = () => {
          input.disabled = true;
        };

        input.addEventListener('touchend', enableInput);
        input.addEventListener('blur', disableInput);

        return () => {
          input.removeEventListener('touchend', enableInput);
          input.removeEventListener('blur', disableInput);
        };
      }
    }
  }, [isMobileDevice, isIOS, map]);

  useEffect(() => {
    if (isDefaultFilters && geocoder) {
      geocoder.clear();
    }
  }, [isDefaultFilters]);

  // @ts-ignore (TS2339) private member
  if (geocoder._map) {
    if (geocoder.getProximity() !== props.proximity && props.proximity !== undefined) {
      geocoder.setProximity(props.proximity);
    }
    if (geocoder.getRenderFunction() !== props.render && props.render !== undefined) {
      geocoder.setRenderFunction(props.render);
    }
    if (geocoder.getLanguage() !== props.language && props.language !== undefined) {
      geocoder.setLanguage(props.language);
    }
    if (geocoder.getZoom() !== props.zoom && props.zoom !== undefined) {
      geocoder.setZoom(props.zoom);
    }
    if (geocoder.getFlyTo() !== props.flyTo && props.flyTo !== undefined) {
      geocoder.setFlyTo(props.flyTo);
    }
    if (geocoder.getPlaceholder() !== props.placeholder && props.placeholder !== undefined) {
      geocoder.setPlaceholder(props.placeholder);
    }
    if (geocoder.getCountries() !== props.countries && props.countries !== undefined) {
      geocoder.setCountries(props.countries);
    }
    if (geocoder.getTypes() !== props.types && props.types !== undefined) {
      geocoder.setTypes(props.types);
    }
    if (geocoder.getMinLength() !== props.minLength && props.minLength !== undefined) {
      geocoder.setMinLength(props.minLength);
    }
    if (geocoder.getLimit() !== props.limit && props.limit !== undefined) {
      geocoder.setLimit(props.limit);
    }
    if (geocoder.getFilter() !== props.filter && props.filter !== undefined) {
      geocoder.setFilter(props.filter);
    }
    if (geocoder.getOrigin() !== props.origin && props.origin !== undefined) {
      geocoder.setOrigin(props.origin);
    }
  }

  return (
    <StyledGeocoder>
      {markerPosition && !isDefaultFilters && (
        <Marker
          key="marker-single"
          longitude={markerPosition?.longitude ?? 0}
          latitude={markerPosition?.latitude ?? 0}
          anchor="bottom"
        >
          <SinglePin dataMarker={0} />
        </Marker>
      )}
    </StyledGeocoder>
  );
}

const noop = () => {};

GeocoderControl.defaultProps = {
  marker: true,
  onLoading: noop,
  onResults: noop,
  onResult: noop,
  onError: noop,
};
